import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { isFreeUser } from './utils/planAccess';
import { FilterIcon, ChevronDownIcon } from '@heroicons/react/outline';

const CustomScenariosPage = () => {
    const [scenarios, setScenarios] = useState([]);
    //delete below
    const [selectedScenario, setSelectedScenario] = useState(null);

    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
    const [selectedScenarioId, setSelectedScenarioId] = useState(null);
    const [expandedScenarioId, setExpandedScenarioId] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState('newest');
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
    const sortOptionLabel = {
        newest: 'Newest First',
        oldest: 'Oldest First',
        az: 'Title A–Z',
      };

    const navigate = useNavigate();

    const toggleExpanded = (scenarioId) => {
        setExpandedScenarioId(prev => prev === scenarioId ? null : scenarioId);
    };
      
    // Helper function to translate profile codes to descriptive names
    const translateProfile = {
        familiarity: {
            HF: 'High Familiarity',
            MF: 'Medium Familiarity',
            LF: 'Low Familiarity'
        },
        emotion: {
            HED: 'High Emotional Display',
            MED: 'Medium Emotional Display',
            LED: 'Low Emotional Display'
        },
        trust: {
            CHEH: 'High Cognitive, High Emotional',
            CHEM: 'High Cognitive, Medium Emotional',
            CHEL: 'High Cognitive, Low Emotional',
            CMEH: 'Medium Cognitive, High Emotional',
            CMEM: 'Medium Cognitive, Medium Emotional',
            CMEL: 'Medium Cognitive, Low Emotional',
            CLEH: 'Low Cognitive, High Emotional',
            CLEM: 'Low Cognitive, Medium Emotional',
            CLEL: 'Low Cognitive, Low Emotional'
        },
        values: {
            SMSE: 'Self Means, Self Ends',
            SMOE: 'Self Means, Other Ends',
            OMSE: 'Other Means, Self Ends',
            OMOE: 'Other Means, Other Ends'
        },
        // Slider profile values mapping
        ew: ['Highly Withdrawn', 'Withdrawn', 'Balanced', 'Engaged', 'Highly Engaged'],
        ac: ['Highly Controlling', 'Controlling', 'Balanced', 'Accepting', 'Highly Accepting'],
        ei: ['Highly Introverted', 'Introverted', 'Balanced', 'Extroverted', 'Highly Extroverted'],
        di: ['Highly Indirect', 'Indirect', 'Balanced', 'Direct', 'Highly Direct'],
        pj: ['Highly Judging', 'Judging', 'Balanced', 'Perceiving', 'Highly Perceiving'],
        rt: ['Highly Risk-Assessing', 'Risk-Assessing', 'Balanced', 'Risk-Taking', 'Highly Risk-Taking'],
        op: ['Highly Pessimistic', 'Pessimistic', 'Balanced', 'Optimistic', 'Highly Optimistic'],
        co: ['Highly Continuity-Oriented', 'Continuity-Oriented', 'Balanced', 'Change-Oriented', 'Highly Change-Oriented'],
        tp: ['Highly Task-Focused', 'Task-Focused', 'Balanced', 'People-Focused', 'Highly People-Focused'],
    };

    // Format timestamp
    const formatTimestamp = (timestamp, justDate = false) => {
        if (!timestamp) return 'N/A';
      
        const dateObj = timestamp._seconds
          ? new Date(timestamp._seconds * 1000)
          : new Date(timestamp);
      
        if (isNaN(dateObj.getTime())) return 'Invalid Date';
      
        return justDate
          ? dateObj.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
          : dateObj.toLocaleString();
    };

    const getDifficultyLabel = (level) => {
        const val = parseInt(level);
        if (val === 1 || val === 2) return 'Beginner';
        if (val === 3) return 'Intermediate';
        if (val === 4 || val === 5) return 'Advanced';
        return 'Unknown';
      };

    useEffect(() => {
        const fetchScenarios = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/scenarios/retrieveUserScenarios/${userId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch scenarios');
                }
                const data = await response.json();
                const sorted = data.sort((a, b) => {
                    const dateA = new Date(a.createdAt?._seconds ? a.createdAt._seconds * 1000 : a.createdAt);
                    const dateB = new Date(b.createdAt?._seconds ? b.createdAt._seconds * 1000 : b.createdAt);
                    return dateB - dateA;
                  });
                  setScenarios(sorted);
            } catch (error) {
                console.error('Error fetching scenarios:', error);
            } finally {
                setLoading(false); // Stop spinner after fetching
            }
        };

        fetchScenarios();
    }, [token, userId]);

    const handleUseCustomScenario = (scenarioId) => {
        navigate('/simulator', { 
            state: { 
                scenarioId, 
                isCustomScenario: true,
                fromPage: true,
             } 
        });
    };
        
    const handleDelete = async () => {
        setIsModalOpen(false);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/scenarios/deleteScenario/${selectedScenarioId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                throw new Error('Failed to delete scenario');
            }
            setScenarios(scenarios.filter(scenario => scenario.scenarioId !== selectedScenarioId));
        } catch (error) {
            console.error('Error deleting scenario:', error);
        }
    };

    const openModal = async (scenarioId) => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/scenarios/checkScenarioUsage/${scenarioId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
      
          const data = await response.json();
      
          if (data.hasConversations) {
            setIsWarningModalOpen(true);
          } else {
            setSelectedScenarioId(scenarioId);
            setIsModalOpen(true);
          }
        } catch (error) {
          console.error("Error checking scenario usage:", error);
          alert("Failed to check if this scenario is deletable.");
        }
    };      

    const closeModal = () => {
        setIsModalOpen(false);
    };

    // Filter and sort custom scenarios based on search term and selected sort option
    const filteredScenarios = scenarios
        .filter((scen) => {
            const search = searchTerm.toLowerCase();
            return (
                scen.title.toLowerCase().includes(search) ||
                scen.content?.summary?.toLowerCase().includes(search)
            );
        })
        .sort((a, b) => {
            const getDate = (obj) =>
              obj.createdAt?._seconds
                ? new Date(obj.createdAt._seconds * 1000)
                : new Date(obj.createdAt);
          
            if (sortOption === 'newest') return getDate(b) - getDate(a);
            if (sortOption === 'oldest') return getDate(a) - getDate(b);
            if (sortOption === 'az') return a.title.localeCompare(b.title);
            return 0;
        });

    //---------------------------------
    //  CONFIRMATION MODAL COMPONENT
    //---------------------------------
    const ConfirmationModal = ({ isOpen, onConfirm, onCancel }) => {
        if (!isOpen) return null;
      
        return (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
                    <h2 className="text-xl font-bold text-gray-800 mb-2">Confirm Deletion</h2>
                    <p className="text-gray-700 mb-4">
                    Are you sure you want to delete this scenario?
                    <br />
                    This action cannot be undone.
                    </p>
                    {isFreeUser() && (
                    <p className="text-xs text-yellow-700 bg-yellow-100 border border-yellow-300 rounded p-2 mb-4">
                        Deleting a scenario does <strong>not</strong> replenish your 3 custom scenario creation limit. Free users are limited by how many scenarios they create, not how many are stored.
                    </p>
                    )}
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={onConfirm}
                            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition"
                        >
                            Delete
                        </button>
                        <button
                            onClick={onCancel}
                            className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const WarningModal = ({ isOpen, onClose }) => {
        if (!isOpen) return null;
      
        return (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
              <h2 className="text-xl font-bold text-red-600 mb-2">Cannot Delete Scenario</h2>
              <p className="text-gray-700 mb-4">
                This scenario has already been used in a conversation and cannot be deleted.
              </p>
              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        );
    };

    const ScenarioDetailsBlock = ({ scenario, translateProfile, formatTimestamp }) => {
        return (
          <div className="p-4 border-t border-gray-200 bg-white text-sm text-gray-700">
      
            <div className="mb-4">
              <h4 className="font-semibold text-gray-800 mb-1">Description</h4>
              <p>{scenario.content?.description}</p>
            </div>
      
            {Array.isArray(scenario.content?.examples) && scenario.content.examples.length > 0 && (
              <div className="mb-4">
                <h4 className="font-semibold text-gray-800 mb-1">Examples</h4>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  {scenario.content.examples.map((ex, idx) => <li key={idx}>{ex}</li>)}
                </ul>
              </div>
            )}
      
            {scenario.profile && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Profile Information</h4>
                <div className="space-y-1">
                  <p><strong>Familiarity:</strong> {translateProfile.familiarity[scenario.profile.familiarity]}</p>
                  <p><strong>Emotion:</strong> {translateProfile.emotion[scenario.profile.emotion]}</p>
                  <p><strong>Trust:</strong> {translateProfile.trust[scenario.profile.trust]}</p>
                  <p><strong>Values:</strong> {translateProfile.values[scenario.profile.values]}</p>
                  <p><strong>Engage/Withdraw:</strong> {translateProfile.ew[scenario.profile.ew - 1]}</p>
                  <p><strong>Accept/Control:</strong> {translateProfile.ac[scenario.profile.ac - 1]}</p>
                  <p><strong>Extrovert/Introvert:</strong> {translateProfile.ei[scenario.profile.ei - 1]}</p>
                  <p><strong>Direct/Indirect:</strong> {translateProfile.di[scenario.profile.di - 1]}</p>
                  <p><strong>Perceive/Judge:</strong> {translateProfile.pj[scenario.profile.pj - 1]}</p>
                  <p><strong>Risk-Take/Risk-Assess:</strong> {translateProfile.rt[scenario.profile.rt - 1]}</p>
                  <p><strong>Optimist/Pessimist:</strong> {translateProfile.op[scenario.profile.op - 1]}</p>
                  <p><strong>Change/Continuity:</strong> {translateProfile.co[scenario.profile.co - 1]}</p>
                  <p><strong>Task/People:</strong> {translateProfile.tp[scenario.profile.tp - 1]}</p>
                </div>
              </div>
            )}
          </div>
        );
      };

    //---------------------------------
    //      MAIN RETURN
    //---------------------------------
    return (
        <div className="w-full min-w-[460px] max-w-4xl mx-auto px-6 py-10 bg-white shadow-md rounded-lg">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-gray-800">Custom Scenarios</h2>
                <button
                    onClick={() => navigate('/create-scenario')}
                    className="bg-emerald-500 hover:bg-emerald-700 text-white font-medium py-2 px-4 rounded transition"
                >
                    Create a New Scenario
                </button>
            </div>


            {/* Search and Sort Controls */}
            <div className="flex items-center justify-between mb-4">
                <input
                    type="text"
                    placeholder="Search by title or employee name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border px-3 py-2 rounded w-2/3 md:w-1/2"
                />

                <div className="relative inline-block text-left ml-4">
                    <button
                        onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                        className="inline-flex justify-center items-center gap-2 border px-3 py-2 rounded bg-white shadow-sm hover:bg-gray-50"
                    >
                        <FilterIcon className="w-5 h-5 text-gray-600" />
                        <span className="text-sm text-gray-700">Sort: {sortOptionLabel[sortOption]}</span>
                        <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                    </button>

                    {sortDropdownOpen && (
                        <div className="absolute z-10 mt-2 w-48 bg-white shadow-lg rounded-md border">
                            {Object.entries(sortOptionLabel).map(([value, label]) => (
                                <button
                                    key={value}
                                    onClick={() => {
                                    setSortOption(value);
                                    setSortDropdownOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Scenarios List */}
            {loading ? (
                <div className="text-center text-gray-600">Loading scenarios...</div>
            ) : (
                filteredScenarios.map((scenario) => {
                    const isExpanded = expandedScenarioId === scenario.scenarioId;

                    return (
                        <div
                            key={scenario.scenarioId}
                            className="bg-white border border-gray-200 rounded-xl shadow-sm mb-4 hover:shadow-md transition-shadow"
                        >
                            <div
                                className="flex flex-wrap justify-between items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 cursor-pointer"
                                onClick={() => toggleExpanded(scenario.scenarioId)}
                            >
                                <div className="flex-1 min-w-[400px]">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <h3 className="text-lg font-semibold text-gray-800">{scenario.title}</h3>
                                        <span className="text-sm text-gray-500">{formatTimestamp(scenario.createdAt, true)}</span>
                                        {scenario.content?.difficulty && (
                                            <span className="bg-white border border-gray-300 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full shadow-sm">
                                                {getDifficultyLabel(scenario.content.difficulty)}
                                            </span>
                                        )}
                                    </div>

                                    <p className="text-sm italic text-gray-600 pr-28">
                                        {scenario.content?.summary
                                            ? scenario.content.summary.split(' ').slice(0, 20).join(' ') + '...'
                                            : 'No summary available.'}
                                    </p>
                                </div>

                                <div className="flex gap-2">
                                    {/* Edit Button */}
                                    {isFreeUser() ? (
                                        <a
                                            href="/pricing"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-sm px-3 py-1 rounded bg-yellow-100 text-yellow-800 hover:bg-yellow-200 transition block text-center"
                                        >
                                            Upgrade to Edit →
                                        </a>
                                        ) : (
                                        <button
                                            onClick={(e) => {
                                            e.stopPropagation();
                                            navigate('/create-scenario', {
                                                state: { isEdit: true, scenarioId: scenario.scenarioId }
                                            });
                                            }}
                                            className="text-sm px-3 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200 transition"
                                        >
                                            Edit
                                        </button>
                                        )}

                                    {/* Use Button */}
                                    <button
                                        onClick={(e) => {
                                        e.stopPropagation();
                                        handleUseCustomScenario(scenario.scenarioId);
                                        }}
                                        className="text-sm px-3 py-1 rounded transition bg-green-100 text-green-700 hover:bg-green-200"
                                    >
                                        Use
                                    </button>

                                    {/* Delete Button */}
                                    <button
                                        onClick={(e) => {
                                        e.stopPropagation();
                                        openModal(scenario.scenarioId);
                                        }}
                                        className="text-sm px-3 py-1 rounded transition bg-red-100 text-red-600 hover:bg-red-200"
                                    >
                                        Delete
                                    </button>
                                </div>

                            </div>

                        {isExpanded && (
                            <ScenarioDetailsBlock
                            scenario={scenario}
                            translateProfile={translateProfile}
                            formatTimestamp={formatTimestamp}
                            />
                        )}
                        </div>
                    );
                })
            )}

            <ConfirmationModal isOpen={isModalOpen} onConfirm={handleDelete} onCancel={closeModal} />
            <WarningModal isOpen={isWarningModalOpen} onClose={() => setIsWarningModalOpen(false)} />
        </div>

    );
};

export default CustomScenariosPage;
